import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";
import { SubmitButton } from "../../components/Input";
import { showToast } from "../../components/toast";
import moment from "moment/moment";

const TicketDetailsForm = () => {
  const [ticket, setTicket] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const [ticketStatus, setTicketStatus] = useState([]);
  const [replyList, setReplyList] = useState([]);
  const location = useLocation();
  const ticketIds = location.state?.ticketIds || [];
  const [formData, setFormData] = useState({
    subscriberName: "",
    subscriberId: "",
    subscriberIdText: "",
    mobileNo: "",
    categoryId: "",
    ticketTitle: "",
    ticketDescription: "",
    ticketReply: "",
    ticketStatus: 1,
    createdOn: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { catid, id } = useParams();
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const currentIndex = ticketIds.findIndex(
    (ticketId) => ticketId === parseInt(id)
  );

  const previousId = ticketIds[currentIndex - 1] ?? null;
  const nextId = ticketIds[currentIndex + 1] ?? null;

  const fetchTicket = async (id, catid) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}Ticket/add/${id}/${catid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data;
      const ticketData = data.ticket;
      setTicket(data.ticket);

      setFormData({
        subscriberName: `${ticketData.subscribername || ""} ( ${ticketData.mobileno || ""
          } )`,
        subscriberId: ticketData.subscriberid,
        subscriberIdText: ticketData.subscriberidtext,
        mobileNo: ticketData.mobileno,
        categoryId: ticketData.categoryid,
        ticketTitle: ticketData.ticket_title,
        ticketDescription: ticketData.ticket_description,
        ticketReply: "",
        ticketStatus: parseInt(ticketData.ticket_status),
        createdOn: ticketData.created_on || "",
      });
      setReplyList(data.replytolist);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketCategory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}Ticket/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setCategoryList(response.data.categoryList);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketCategory();
    if (id && catid) {
      fetchTicket(id, catid);
    }

    setTicketStatus([
      { value: 0, label: "Requested" },
      { value: 1, label: "Open" },
      { value: 2, label: "Closed" },
    ]);
  }, [id, catid]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ticket_reply: formData.ticketReply,
      ticket_status: parseInt(formData.ticketStatus),
    };

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}Ticket/reply/${catid}/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success === true) {
        showToast.success(response.data.message);
        navigate(`/Ticket/Main/tickets/${catid}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      <div id="content">{loading && <LoadingSpinner />}</div>
      <div className="panel  panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between pt-2">
          <div className="panel-heading border-0">
            <span className="panel-title">
              Ticket - {moment(formData.createdOn).format("DD-MM-YYYY hh:mm A")}
            </span>
          </div>
          <div className="me-3">
            {previousId !== 0 && previousId !== "" && previousId !== null && (
              <button
                type="button"
                className="btn btn-default btn-sm"
                title="Previous Ticket"
                onClick={() =>
                  navigate(`/Ticket/Main/tickets/${catid}/edit/${previousId}`, {
                    state: { ticketIds },
                  })
                }
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            &nbsp;
            {nextId !== 0 && nextId !== "" && nextId !== null && (
              <button
                type="button"
                className="btn btn-default btn-sm"
                title="Next Ticket"
                onClick={() =>
                  navigate(`/Ticket/Main/tickets/${catid}/edit/${nextId}`, {
                    state: { ticketIds },
                  })
                }
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
          </div>
        </div>
        <div className="panel-body">
          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>
                  Subscriber Name{" "}
                  <Link
                    to={`/Subscriber/edit/${formData.subscriberId}`}
                    target="_blank"
                    className="text-primary"
                  >
                    {`${formData.subscriberIdText || ""}`}
                  </Link>
                </label>
                <input
                  type="text"
                  name="subscriberName"
                  className="form-control"
                  value={formData.subscriberName}
                  onChange={handleInputChange}
                  disabled
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Category</label>
                <select
                  name="categoryId"
                  className="form-control"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  disabled
                >
                  {categoryList.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-12">
              <div className="form-group">
                <label>Query Title</label>
                <input
                  type="text"
                  name="ticketTitle"
                  className="form-control"
                  value={formData.ticketTitle}
                  onChange={handleInputChange}
                  disabled
                />
              </div>
            </div>

            <div className="col-md-12">
              <fieldset className="form-group">
                <label>Query Explanation</label>
                <textarea
                  name="ticketDescription"
                  className="form-control"
                  value={formData.ticketDescription}
                  onChange={handleInputChange}
                  disabled
                />
              </fieldset>
            </div>

            <div className="col-md-12">
              <fieldset className="form-group">
                <label>Reply to Ticket</label>
                <textarea
                  name="ticketReply"
                  className="form-control"
                  value={formData.ticketReply}
                  placeholder="Enter Ticket Reply"
                  onChange={handleInputChange}
                />
              </fieldset>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Status</label>
                <select
                  name="ticketStatus"
                  className="form-control"
                  value={formData.ticketStatus}
                  onChange={handleInputChange}
                >
                  {ticketStatus.map((status, index) => (
                    <option key={index} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {ticket && ticket.attachments && (
              <div className="d-flex col-md-12">
                <Link to={ticket.attachments} className="html5lightbox" target="_blank">
                  <img
                    src={ticket.attachments}
                    alt="Attachment"
                    className="img-responsive"
                    style={{ width: "100px" }}
                  />
                </Link>
              </div>
            )}

            <div className="col-12">
              <SubmitButton
                handleSubmit={handleSubmit}
                pageBack={`/Ticket/Main/tickets/${catid}`}
              />
            </div>
          </div>
        </div>
      </div>

      {replyList.map((reply, index) => (
        <div
          key={index}
          className="panel panel-default with-rounded-corner with-shadow"
        >
          <div className="post">
            <div className="post-header">
              <div className="post-header-img">
                {/* <img src={reply.user_image} alt={reply.user_fullname} /> */}
              </div>
              <div className="post-header-info">
                <h4>
                  <a href="#">{reply.user_fullname}</a>
                </h4>
                <span className="time">
                  {moment(reply.created_on).format("DD-MM-YYYY hh:mm A")}
                </span>
              </div>
            </div>
            <div className="post-content">
              <p className="post-desc">{reply.reply_description}</p>
            </div>
          </div>
        </div>
      ))}

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default TicketDetailsForm;
